import memoize from 'memoize-one';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { COUNTRIES, DEFAULT_CURRENCY } from 'my-core/constants';
import { studySessionCheckoutStepViewed, studySessionPurchased } from 'my-core/gtm-events';
import { useCurrentUser, useStandardApiRequest } from 'my-core/hooks';

import StudySessionCheckout from './StudySessionCheckout';

import { confirmStudySessionPurchase } from 'my-actions/StudySessionUserActions';

export default function StudySessionCheckoutCtnr({ courseId, noDialog, open, studySessionId, ...rest }) {
  const currentUser = useCurrentUser();

  const [activeStep, setActiveStep] = useState(0);

  const studySession = useSelector(state =>
    getStudySession(studySessionId, courseId, state.studySessions.items, state.courses.items),
  );

  const course = studySession?.course;
  const pricingInfo = useMemo(
    () => ({
      countryCode: course?.country_code || currentUser.country_code,
      currency: course?.currency || COUNTRIES[currentUser.country_code]?.currency || DEFAULT_CURRENCY,
      salesTax: Number(course?.currency ? course.sales_tax : COUNTRIES[currentUser.country_code]?.sales_tax) || 0,
    }),
    [currentUser.country_code, course],
  );

  const { performRequest: performConfirmPurchase, requestStatus: confirmPurchaseStatus } = useStandardApiRequest({
    actionCreator: confirmStudySessionPurchase,
    errorMessage: true,
    onSuccess: () => setActiveStep(1),
  });

  const purchaseDetails = useRef();
  const handlePayment = useCallback(
    ({ currency, discountCode, paymentIntent, total }) => {
      purchaseDetails.current = { total, discountCode, currency };
      performConfirmPurchase(studySessionId, paymentIntent?.id, discountCode?.id, studySession?.course?.id);
    },
    [performConfirmPurchase, studySessionId, studySession?.course?.id],
  );
  const studySessionUser = useSelector(state => state.studySessionUsers.items[confirmPurchaseStatus?.id]);

  // Tracking Events
  useEffect(() => {
    if ((open || noDialog) && studySession) {
      const step = activeStep === 0 ? 'payment' : 'confirmation';
      studySessionCheckoutStepViewed(studySession, step, pricingInfo.currency);
      if (step === 'confirmation') {
        studySessionPurchased(studySession, studySessionUser, purchaseDetails.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, studySession?.id, activeStep]);

  return (
    <StudySessionCheckout
      {...rest}
      activeStep={activeStep}
      confirming={!!confirmPurchaseStatus?.fetching}
      currentUser={currentUser}
      noDialog={noDialog}
      onChangeStep={setActiveStep}
      onPaymentSuccess={handlePayment}
      open={open}
      pricingInfo={pricingInfo}
      studySession={studySession}
      studySessionUser={studySessionUser}
    />
  );
}

const getStudySession = memoize((id, cId, studySessions, courses) => {
  const ss = studySessions[id];
  const course = courses[cId];
  return ss && { ...ss, course };
});

// StudySessionCheckoutCtnr.propTypes = {
//   open: PropTypes.bool,
//   onClose: PropTypes.func.isRequired,
//   studySessionId: PropTypes.number.isRequired,
//   courseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
// };
